import { createContext, useContext, type ReactNode } from "react";

import type { Action, Lens, RailFamily, SessionCapabilities } from "./types";

/**
 * The capabilities-gating layer the whole app uses. The frontend renders what the server
 * allows; it never decides access (UX-005 §2.1). The value is supplied by SessionGate after
 * bootstrapping /v1/session/capabilities (stub today; Disha's api-client hook later).
 */
const CapabilitiesContext = createContext<SessionCapabilities | null>(null);

export function CapabilitiesProvider({
  value,
  children,
}: {
  value: SessionCapabilities;
  children: ReactNode;
}) {
  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>;
}

export function useCapabilities(): SessionCapabilities {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) throw new Error("useCapabilities must be used within <CapabilitiesProvider>");
  return ctx;
}

/** A data class is visible only if its lens is in capabilities.lenses. */
export function useHasLens(lens: Lens): boolean {
  return useCapabilities().lenses.includes(lens);
}

/**
 * `useHasLens` for a screen that must ALSO render where there is no session.
 *
 * The edge artifact (X3) mounts `EdgeLayout`, which has no
 * `CapabilitiesProvider` — only `SessionGate` provides one — so a shared screen
 * such as Site: Now appears in both trees. `useCapabilities` throws by design, and
 * rightly so for anything that genuinely needs a session; a dual-tree component
 * needs this instead, or it takes the whole edge screen down.
 *
 * Absent capabilities answer FALSE: a lens is an entitlement, so the conservative
 * reading is that it has not been granted, and a money figure stays hidden rather
 * than being shown unentitled.
 */
export function useHasLensOptional(lens: Lens): boolean {
  const ctx = useContext(CapabilitiesContext);
  return ctx ? ctx.lenses.includes(lens) : false;
}

/** Rail family state: undefined = not entitled (absent); {visible, locked} otherwise. */
export function useRailFamily(name: string): RailFamily | undefined {
  return useCapabilities().rail_families.find((f) => f.name === name);
}

/** True if the user's authority envelope permits this action (UX-001 §4). */
export function useCanAction(action: Action): boolean {
  const { authority } = useCapabilities();
  if (authority.limits.length === 0) return true;
  return authority.limits.some((l) => l.action === action);
}

/** True if the entitlement flag is present and truthy (UX-001 §7 tier gating). */
export function useEntitlement(flag: string): boolean {
  return useCapabilities().entitlements[flag] === true;
}
